/**
 * Payment Method Service
 * 
 * Handles payment method storage and management via the internal API.
 * 
 * ## Key Features
 * - Save payment tokens for future automatic charging
 * - Encrypted storage with bcrypt hashing
 * - Retrieve user's saved payment methods
 * 
 * ## Client split
 * 
 * The two **card-save** endpoints (`/token`, `/confirm`) are on `apiClient` and
 * return the envelope. `legacyApiClient` throws on any non-2xx and replaces the
 * body with `"Server error: 500"`, which is how a member ended up reading that
 * instead of the reason the API had already computed — see
 * `docs/refactors/payment-retry-amplification.md`. It also retries 5xx, so each
 * failed card save became four POSTs of a single-use token.
 * 
 * `/setup-intent` and the list endpoint stay on `legacyApiClient` for now: their
 * bodies are still bare, and `/setup-intent`'s `clientSecret`/`publishableKey`
 * are read at the top level by the shared `@butter/ui` hook that all three apps
 * use, so it converts with that hook, not here.
 * @module payment/api
 */

import { apiClient, legacyApiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import { reportPaymentClientError } from '../monitoring';
import type { ApiResult } from '~/lib/apiClient';
import type {
  PaymentMethod,
  SavePaymentMethodRequest,
  SavePaymentMethodPayload,
  CreateSetupIntentResponse,
  ConfirmSetupIntentPayload,
} from '../types';

/**
 * Payment Method Service
 * 
 * Handles payment method storage and retrieval
 */
export const paymentMethodService = {
  /**
   * Save payment method token to database
   * 
   * This stores the encrypted token for future automatic charging.
   * The token is saved with:
   * - Bcrypt hash for verification (one-way)
   * - Encrypted original token for charging (reversible with encryption key)
   * 
   * **IMPORTANT**: Call this AFTER successful booking creation.
   * If this fails, the booking still succeeds (don't fail the transaction).
   * 
   * @param userId - User ID
   * @param request - Payment method details
   * @returns SavePaymentMethodResponse with legacy format
   * 
   * @example
   * ```typescript
   * // Save payment method after booking
   * const result = await paymentMethodService.savePaymentMethod(123, {
   *   provider: 'square',
   *   clubId: 1,
   *   paymentMethodToken: 'cnon:xxx'
   * });
   * 
   * if (result.success) {
   *   console.log('Payment method saved:', result.payload.paymentMethodId);
   * } else {
   *   // Don't fail - booking already created
   *   console.warn('Failed to save payment method');
   * }
   * ```
   */
  async savePaymentMethod(
    userId: number,
    request: SavePaymentMethodRequest
  ): Promise<ApiResult<SavePaymentMethodPayload>> {
    const tenantHandle = appRuntime.tenantHandle;

    return apiClient.post<SavePaymentMethodPayload>(
      `/payment-methods/user/${userId}/token`,
      request,
      {
        tenantHandle: tenantHandle || undefined,
        // `retries: 0`: apiClient returns enveloped failures without retrying,
        // but it still retries THROWN ApiErrors — including a PARSE_ERROR that
        // carries the origin status, which is exactly a Fly 502 with an HTML
        // body during a rolling deploy. This POST spends a single-use token, so
        // a retry cannot succeed and may double-charge.
        retries: 0,
      }
    );
  },

  /**
   * Create a Stripe SetupIntent for the client-confirm save flow (step 1).
   *
   * Returns `{ clientSecret, publishableKey }` for the user's HOME club — the
   * client mounts the Payment Element with `publishableKey` and confirms via
   * `stripe.confirmSetup(…)`. The instrument type is NOT client-chosen: the server
   * resolves the intent's `payment_method_types` (card, or card + bank for the
   * accordion), so this sends no `methodType`.
   *
   * @param userId - User whose HOME club + customer the intent is created against
   */
  async createSetupIntent(userId: number): Promise<CreateSetupIntentResponse> {
    const tenantHandle = appRuntime.tenantHandle;

    return legacyApiClient.post<CreateSetupIntentResponse>(
      `/payment-methods/user/${userId}/setup-intent`,
      {},
      {
        tenantHandle: tenantHandle || undefined,
      }
    );
  },

  /**
   * Confirm a client-confirmed SetupIntent and persist the instrument (step 3).
   *
   * The backend re-verifies the intent server-side (succeeded + customer-owned)
   * before storing — a raw `pm_…` is never trusted. Returns the DB row id.
   *
   * @param userId - User whose HOME club the intent must belong to
   * @param setupIntentId - The `seti_…` id returned by `stripe.confirmSetup`
   */
  async confirmSetupIntent(
    userId: number,
    setupIntentId: string
  ): Promise<ApiResult<ConfirmSetupIntentPayload>> {
    const tenantHandle = appRuntime.tenantHandle;

    return apiClient.post<ConfirmSetupIntentPayload>(
      `/payment-methods/user/${userId}/confirm`,
      { setupIntentId },
      {
        tenantHandle: tenantHandle || undefined,
        // `retries: 0`: apiClient returns enveloped failures without retrying,
        // but it still retries THROWN ApiErrors — including a PARSE_ERROR that
        // carries the origin status, which is exactly a Fly 502 with an HTML
        // body during a rolling deploy. This POST spends a single-use token, so
        // a retry cannot succeed and may double-charge.
        retries: 0,
      }
    );
  },

  /**
   * Set one of the user's stored methods as their default (the card that bills
   * their membership). Used by the account-setup page so an imported member can
   * pick their default pre-login.
   *
   * Unlike the sibling methods above, this endpoint IS migrated to the envelope,
   * so it uses the canonical `apiClient` (data-or-throw). Guest-writable: the
   * `tenantHandle` sends `X-Tenant-Id` and `skipAuth` keeps it a clean pre-login
   * call (no bearer). Pass `tenantHandle` explicitly on the account-setup page —
   * `appRuntime` can be stale there.
   *
   * @param userId - The member whose default is being set
   * @param paymentMethodId - DB id (or encrypted token) of the target method
   * @param tenantHandle - Tenant handle for the `X-Tenant-Id` header (falls back to appRuntime)
   */
  async setDefaultPaymentMethod(
    userId: number,
    paymentMethodId: number | string,
    tenantHandle?: string
  ): Promise<void> {
    const result = await apiClient.put(
      `/payment-methods/user/${userId}/default`,
      { paymentMethodId: String(paymentMethodId) },
      {
        tenantHandle: tenantHandle ?? appRuntime.tenantHandle ?? undefined,
        skipAuth: true,
      }
    );

    if (!result.success) {
      throw new Error(result.message || 'Failed to set default payment method');
    }
  },

  /**
   * Get user's saved payment methods
   *
   * Retrieves all active payment methods for a user.
   * 
   * @param userId - User ID
   * @returns Object with paymentMethods array (legacy format)
   * 
   * @example
   * ```typescript
   * const result = await paymentMethodService.getPaymentMethods(123);
   * const methods = result.paymentMethods;
   * const defaultMethod = methods.find(m => m.isDefault);
   * ```
   */
  async getPaymentMethods(
    userId: number
  ): Promise<{ paymentMethods: PaymentMethod[] }> {
    const tenantHandle = appRuntime.tenantHandle;

    return legacyApiClient.get<{ paymentMethods: PaymentMethod[] }>(
      `/payment-methods/user/${userId}`,
      {
        tenantHandle: tenantHandle || undefined,
      }
    );
  },

  /**
   * Get a specific payment method by ID
   * 
   * Retrieves a single payment method's details (for displaying last 4 digits, etc).
   * 
   * @param userId - User ID
   * @param paymentMethodId - Payment method ID
   * @returns Payment method or null if not found
   * 
   * @example
   * ```typescript
   * const method = await paymentMethodService.getPaymentMethodById(123, 7);
   * if (method) {
   *   console.log(`Card ending in ${method.last4}`);
   * }
   * ```
   */
  async getPaymentMethodById(
    userId: number,
    paymentMethodId: number
  ): Promise<PaymentMethod | null> {
    const tenantHandle = appRuntime.tenantHandle;

    try {
      const result = await legacyApiClient.get<{ paymentMethods: PaymentMethod[] }>(
        `/payment-methods/user/${userId}`,
        {
          tenantHandle: tenantHandle || undefined,
        }
      );

      const method = result.paymentMethods.find(pm => pm.id === paymentMethodId);
      return method || null;
    } catch (error) {
      console.error('[paymentMethodService] Error fetching payment method:', error);
      // Degrades to "no card" in the UI (e.g. confirmation-page last-4). Also
      // captured generically by the apiClient layer; this domain-tagged warning
      // makes the cosmetic-read failure filterable during onboarding.
      reportPaymentClientError('load_payment_method_by_id', error, {
        kind: 'response_anomaly',
        level: 'warning',
        surface: 'web_payment_method_read',
        extra: { userId, paymentMethodId },
      });
      return null;
    }
  },
};
